import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Rows3, ShieldCheck, FileText, Download } from "lucide-react";
import QuoteComments from "../components/os/QuoteComments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicQuoteApproval({ apiBase = "/crm/public/quote" }) {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [approving, setApproving] = useState(false);
  const [done, setDone] = useState(false);

  const load = () => axios.get(`${API}${apiBase}/${token}`)
    .then((r) => setData(r.data)).catch(() => setErr("Este presupuesto no está disponible o el enlace no es válido."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const approve = async () => {
    setApproving(true);
    try { await axios.post(`${API}${apiBase}/${token}/approve`); setDone(true); }
    catch (e) { setErr("No se pudo registrar la aprobación. Intenta más tarde."); }
    finally { setApproving(false); }
  };

  if (err) return <Shell><div className="text-center text-navy/60 py-10" data-testid="approve-error">{err}</div></Shell>;
  if (!data) return <Shell><div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet" /></div></Shell>;

  const q = data.quote || { items: [], sections: [] };
  const terms = data.terms || {};
  const accent = data.accent_color || "#7c5cff";
  const pdfUrl = `${API}${apiBase}/${token}/pdf`;
  const logoUrl = data.has_logo ? `${API}${apiBase}/${token}/logo` : null;
  const approved = done || data.status === "approved" || data.already_converted;
  const gross = Number(q.subtotal || 0) + Number(q.discount_amount || 0);
  const itemNet = (it) => Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount_pct || 0) / 100);
  // Filas con subtotal por sección (igual que el PDF): al cerrar cada sección se inserta su total.
  const hasSection = (q.items || []).some((i) => i.kind === "header");
  const renderRows = [];
  { let curLabel = null, curSum = 0;
    const flush = () => { if (curLabel !== null) renderRows.push({ kind: "subtotal", label: curLabel, amount: curSum }); curSum = 0; };
    (q.items || []).forEach((it) => {
      if (it.kind === "header") { flush(); curLabel = it.description || ""; renderRows.push({ kind: "header", it }); }
      else { curSum += itemNet(it); renderRows.push({ kind: "item", it }); }
    });
    flush();
  }
  const termRows = [
    ["Fecha de elaboración", terms.fecha],
    ["Condiciones de pago", terms.payment_terms],
    ["Cuentas / datos de pago", terms.payment_accounts],
    ["Garantía", terms.warranty],
    ["Vigencia", terms.validity],
    ["Otras condiciones / notas", terms.conditions],
  ].filter(([, v]) => v);
  const headerRows = [
    ["Cliente", terms.h_cliente], ["Cuenta", terms.h_cuenta], ["Contacto", terms.h_contacto],
    ["Versión", terms.h_version], ["Elaboró", terms.h_elaboro], ["Tipo de servicio", terms.h_tipo_servicio],
    ["Proyecto", terms.h_proyecto], ["Locación / Plaza", terms.h_locacion], ["Fecha de ejecución", terms.h_fecha_ejecucion],
    ["Otros detalles", terms.h_otros],
  ].filter(([, v]) => v);

  return (
    <Shell accent={accent}>
      {logoUrl && <img src={logoUrl} alt="Logo" data-testid="approve-logo" className="h-14 max-w-[220px] object-contain mb-5" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-wide text-navy/40">Presupuesto de</div>
          <h1 className="text-2xl font-medium text-navy" data-testid="approve-issuer">{data.issuer}</h1>
          <div className="text-sm text-navy/50" data-testid="approve-subtitle">Cliente: {data.client}{data.project_name ? ` · Proyecto: ${data.project_name}` : ""}{q.folio ? ` · Folio ${q.folio}` : ""}</div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {approved && <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Aprobado</span>}
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="approve-pdf" className="text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 hover:opacity-80" style={{ borderColor: accent, color: accent }}><Download className="w-3.5 h-3.5" /> Descargar PDF</a>
        </div>
      </div>

      {headerRows.length > 0 && (
        <div className="rounded-xl border border-navy/10 p-4 mb-5 grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="approve-header">
          {headerRows.map(([l, v]) => (
            <div key={l} className="text-sm flex gap-2">
              <span className="text-navy/40 shrink-0">{l}:</span>
              <span className="text-navy/80">{v}</span>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-navy/10 overflow-hidden mb-5" data-testid="approve-items">
        <div className="grid grid-cols-[1fr_60px_110px_110px] gap-2 bg-navy text-white text-xs px-4 py-2.5">
          <span>Concepto</span><span className="text-right">Cant.</span><span className="text-right">P. Unitario</span><span className="text-right">Importe</span>
        </div>
        {renderRows.map((r, i) => r.kind === "header" ? (
          <div key={i}>
            <div className="font-semibold text-sm px-4 py-2 uppercase tracking-wide flex items-center gap-2" style={{ backgroundColor: `${accent}1a`, color: accent }}><Rows3 className="w-3.5 h-3.5" /> {r.it.description}</div>
            {(r.it.images || []).length > 0 && (
              <div className="bg-white px-4 py-3 border-b border-navy/5 flex flex-wrap gap-2" data-testid="approve-section-images">
                {(r.it.images || []).map((src, k) => <img key={k} src={src} alt="" className="h-24 w-24 object-cover rounded-lg border border-navy/10" />)}
              </div>
            )}
          </div>
        ) : r.kind === "subtotal" ? (
          <div key={i} data-testid="approve-section-subtotal" className="grid grid-cols-[1fr_60px_110px_110px] gap-2 px-4 py-2 text-sm border-b border-navy/5 font-semibold" style={{ color: accent, backgroundColor: `${accent}0d` }}>
            <span>Subtotal {r.label}</span><span /><span /><span className="text-right">{money(r.amount)}</span>
          </div>
        ) : (
          <div key={i} className="grid grid-cols-[1fr_60px_110px_110px] gap-2 px-4 py-2.5 text-sm border-b border-navy/5">
            <span className="text-navy/80">
              {r.it.description}{Number(r.it.discount_pct) ? ` (−${r.it.discount_pct}%)` : ""}
              {(r.it.unit || r.it.product_key || r.it.sku) && (
                <span className="block text-[11px] text-navy/40 mt-0.5">
                  {[r.it.unit && `Unidad: ${r.it.unit}`, r.it.product_key && `Clave SAT: ${r.it.product_key}`, r.it.sku && `Cód. interno: ${r.it.sku}`].filter(Boolean).join("  ·  ")}
                </span>
              )}
              {(r.it.images || []).length > 0 && (
                <span className="flex flex-wrap gap-2 mt-2">
                  {(r.it.images || []).map((src, k) => <img key={k} src={src} alt="" className="h-20 w-20 object-cover rounded-lg border border-navy/10" />)}
                </span>
              )}
            </span>
            <span className="text-right text-navy/50">{r.it.qty}</span>
            <span className="text-right text-navy/50">{money(r.it.unit_price)}</span>
            <span className="text-right text-navy/80">{money(itemNet(r.it))}</span>
          </div>
        ))}
      </div>

      {!q.hide_total && (
      <div className="flex justify-end mb-6">
        <div className="w-72 space-y-1 text-sm">
          <Row l="Subtotal" v={money(gross)} />
          {q.discount_amount ? <Row l="Descuentos" v={`− ${money(q.discount_amount)}`} /> : null}
          {q.margin_amount ? <Row l={`Margen (${q.margin_pct}%)`} v={money(q.margin_amount)} /> : null}
          {q.tax_amount ? <Row l={q.tax_included ? `IVA incluido (${q.tax_pct}%)` : `IVA (${q.tax_pct}%)`} v={money(q.tax_amount)} /> : null}
          <div className="flex justify-between border-t border-navy/10 pt-2 mt-1 text-lg font-medium text-navy"><span>Total</span><span data-testid="approve-total">{money(q.total)}</span></div>
        </div>
      </div>
      )}

      {termRows.length > 0 && (
        <div className="rounded-xl border border-navy/10 p-5 mb-8" data-testid="approve-terms">
          <div className="text-sm font-medium text-navy flex items-center gap-1.5 mb-3"><FileText className="w-4 h-4" style={{ color: accent }} /> Condiciones</div>
          <div className="space-y-3">
            {termRows.map(([l, v]) => (
              <div key={l}>
                <div className="text-[11px] uppercase tracking-wide" style={{ color: accent }}>{l}</div>
                <div className="text-sm text-navy/80 whitespace-pre-line">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {approved ? (
        <div className="text-center py-6 rounded-xl bg-emerald-500/5 border border-emerald-500/20" data-testid="approve-success">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
          <p className="font-medium text-navy">¡Gracias! El presupuesto quedó aprobado.</p>
          <p className="text-sm text-navy/50">El equipo iniciará tu proyecto en breve.</p>
        </div>
      ) : (
        <button data-testid="approve-btn" onClick={approve} disabled={approving} className="w-full rounded-full text-white py-4 text-lg hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2" style={{ backgroundColor: accent }}>
          {approving ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />} Aprobar presupuesto
        </button>
      )}

      <div className="mt-8 pt-6 border-t border-navy/10">
        <QuoteComments path={`${apiBase}/${token}/comments`} role="client" accent={accent} isPublic />
      </div>
    </Shell>
  );
}

const Row = ({ l, v }) => <div className="flex justify-between text-navy/60"><span>{l}</span><span>{v}</span></div>;

function Shell({ children, accent }) {
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg border border-navy/5 p-8" style={accent ? { borderTop: `4px solid ${accent}` } : undefined}>{children}</div>
    </div>
  );
}
